import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  CreditCard as CreditCardIcon,
  Print as PrintIcon,
  PointOfSale as PointOfSaleIcon,
  Wifi as WifiIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";

import PageShell from "../../../../Components/PageShell/PageShell";
import {
  getSystemDeviceHardwareList,
  getSystemDeviceReaderStatus,
} from "../../../../store/actions";
import { readerHealthMeta } from "./readerHealth";

// Response rows are wrapped as { SystemDevice: {...} } by the backend helper.
const unwrap = (list) =>
  (Array.isArray(list) ? list : [])
    .map((row) => row?.SystemDevice || row)
    .filter(Boolean);

const truthyFlag = (v) => v === 1 || v === "1" || v === true;

function TypeBadges({ device }) {
  const badges = [];
  if (truthyFlag(device.FlagPaymentTerminal))
    badges.push({ label: "Payment Terminal", icon: <CreditCardIcon sx={{ fontSize: 13 }} />, color: "#2563eb", bg: "#eff6ff" });
  if (truthyFlag(device.FlagPosTerminal))
    badges.push({ label: "POS Terminal", icon: <PointOfSaleIcon sx={{ fontSize: 13 }} />, color: "#7c3aed", bg: "#f5f3ff" });
  if (truthyFlag(device.FlagPosPrinter))
    badges.push({ label: "Printer", icon: <PrintIcon sx={{ fontSize: 13 }} />, color: "#0f766e", bg: "#f0fdfa" });

  if (!badges.length)
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );

  return (
    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
      {badges.map((b) => (
        <Chip
          key={b.label}
          icon={b.icon}
          label={b.label}
          size="small"
          sx={{
            fontSize: 11,
            height: 22,
            fontWeight: 600,
            color: b.color,
            bgcolor: b.bg,
            "& .MuiChip-icon": { color: b.color, ml: "6px" },
          }}
        />
      ))}
    </Stack>
  );
}

export default function SystemDeviceHardwareSettings() {
  const dispatch = useDispatch();
  const history = useHistory();

  const { listResponse, listLoading, readerStatusById, readerStatusLoadingById } =
    useSelector((state) => ({
      listResponse: state.SystemDeviceHardware?.list,
      listLoading: state.SystemDeviceHardware?.listLoading,
      readerStatusById: state.SystemDeviceHardware?.readerStatusById || {},
      readerStatusLoadingById:
        state.SystemDeviceHardware?.readerStatusLoadingById || {},
    }));

  useEffect(() => {
    dispatch(getSystemDeviceHardwareList());
  }, [dispatch]);

  const devices = useMemo(
    () => unwrap(listResponse?.systemdevice_list),
    [listResponse]
  );

  const [search, setSearch] = useState("");
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return devices;
    return devices.filter((d) =>
      `${d.Name || ""} ${d.Description || ""}`.toLowerCase().includes(q)
    );
  }, [devices, search]);

  const paymentTerminalCount = useMemo(
    () => devices.filter((d) => truthyFlag(d.FlagPaymentTerminal)).length,
    [devices]
  );

  const goToDevice = (id) => history.push(`/system-device/${id}/Overview`);

  const checkReader = (e, id) => {
    e.stopPropagation();
    dispatch(getSystemDeviceReaderStatus({ id }));
  };

  const stats = [
    { label: "Devices", value: devices.length },
    { label: "Payment Terminals", value: paymentTerminalCount },
  ];

  return (
    <PageShell
      title="POS Hardware"
      description="Physical POS devices: card readers, POS terminals and printers. Check a payment terminal's live Stripe reader status, or open a device for full details."
      stats={stats}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 1.5,
          py: 0.75,
          mb: 2,
          maxWidth: 360,
          borderRadius: 999,
          border: "1px solid rgba(148,163,184,0.3)",
          bgcolor: "#fff",
        }}
      >
        <i className="ri-search-line" style={{ color: "#94a3b8" }} />
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search devices…"
          style={{ border: "none", outline: "none", width: "100%", background: "transparent" }}
        />
      </Box>

      <Box
        sx={{
          border: "1px solid rgba(148,163,184,0.25)",
          borderRadius: 2,
          overflow: "hidden",
          bgcolor: "#fff",
        }}
      >
        {/* header row */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "60px 1.4fr 1.6fr 1.4fr 90px",
            gap: 1,
            px: 2,
            py: 1.25,
            bgcolor: "#0f172a",
            color: "#e2e8f0",
            fontSize: 12,
            fontWeight: 800,
            letterSpacing: 0.3,
            textTransform: "uppercase",
          }}
        >
          <span>ID</span>
          <span>Name</span>
          <span>Type</span>
          <span>Reader Status</span>
          <span />
        </Box>

        {listLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
            <CircularProgress />
          </Box>
        ) : filtered.length === 0 ? (
          <Box sx={{ p: 5, textAlign: "center" }}>
            <Typography color="text.secondary">No devices found.</Typography>
          </Box>
        ) : (
          filtered.map((device) => {
            const id = device.Id;
            const isPaymentTerminal = truthyFlag(device.FlagPaymentTerminal);
            const status = readerStatusById[id]?.reader_status;
            const statusLoading = readerStatusLoadingById[id];
            const meta = status ? readerHealthMeta(status.health) : null;

            return (
              <Box
                key={id}
                onClick={() => goToDevice(id)}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "60px 1.4fr 1.6fr 1.4fr 90px",
                  gap: 1,
                  px: 2,
                  py: 1.5,
                  alignItems: "center",
                  cursor: "pointer",
                  borderTop: "1px solid rgba(148,163,184,0.18)",
                  borderTopStyle: "solid",
                  "&:hover": { bgcolor: "#f8fafc" },
                }}
              >
                <Typography sx={{ fontWeight: 700, color: "#2563eb", fontSize: 13 }}>
                  #{id}
                </Typography>

                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: 13.5 }}>
                    {device.Name || <em style={{ color: "#94a3b8" }}>Unnamed device</em>}
                  </Typography>
                  {device.Description ? (
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
                      {device.Description}
                    </Typography>
                  ) : null}
                </Box>

                <TypeBadges device={device} />

                <Box>
                  {!isPaymentTerminal ? (
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
                      —
                    </Typography>
                  ) : statusLoading ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CircularProgress size={14} />
                      <Typography variant="body2" sx={{ fontSize: 12 }}>
                        Checking…
                      </Typography>
                    </Stack>
                  ) : status ? (
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Chip
                        label={meta.label}
                        size="small"
                        sx={{
                          fontSize: 11,
                          height: 22,
                          fontWeight: 700,
                          color: meta.color,
                          bgcolor: meta.bg,
                        }}
                      />
                      {typeof status.stale_seconds === "number" ? (
                        <Typography variant="caption" color="text.secondary">
                          seen {status.stale_seconds}s ago
                        </Typography>
                      ) : null}
                    </Stack>
                  ) : (
                    <Tooltip title="Check live Stripe reader status">
                      <button
                        type="button"
                        onClick={(e) => checkReader(e, id)}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          padding: "5px 10px",
                          borderRadius: 999,
                          border: "1px solid rgba(37,99,235,0.35)",
                          background: "#eff6ff",
                          color: "#2563eb",
                          fontSize: 12,
                          fontWeight: 700,
                          cursor: "pointer",
                        }}
                      >
                        <WifiIcon sx={{ fontSize: 14 }} />
                        Check reader
                      </button>
                    </Tooltip>
                  )}
                </Box>

                <Stack direction="row" justifyContent="flex-end">
                  {isPaymentTerminal && status ? (
                    <Tooltip title="Re-check reader">
                      <IconButton
                        size="small"
                        onClick={(e) => checkReader(e, id)}
                        sx={{ color: "#2563eb" }}
                      >
                        <WifiIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  ) : null}
                  <Tooltip title="Open device">
                    <IconButton size="small" onClick={() => goToDevice(id)}>
                      <ChevronRightIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Box>
            );
          })
        )}
      </Box>
    </PageShell>
  );
}
